import type { ParseResult, Transaction } from "../types";

export interface CsvOptions {
  /** Field separator. Default ",". Use ";" for locales where comma is decimal. */
  delimiter?: string;
  /** Include a header row. Default true. */
  header?: boolean;
  /** Include the running balance column. Default true when any row has it. */
  includeBalance?: boolean;
}

const COLUMNS = ["date", "valueDate", "description", "amount", "balance"] as const;
const HEADERS: Record<(typeof COLUMNS)[number], string> = {
  date: "Date",
  valueDate: "Value date",
  description: "Description",
  amount: "Amount",
  balance: "Balance",
};

export function toCsv(result: ParseResult, opts: CsvOptions = {}): string {
  const delimiter = opts.delimiter ?? ",";
  const header = opts.header ?? true;
  const hasBalance =
    opts.includeBalance ?? result.transactions.some((t) => t.balance != null);
  const hasValueDate = result.transactions.some((t) => t.valueDate != null);

  const cols = COLUMNS.filter((c) => {
    if (c === "balance") return hasBalance;
    if (c === "valueDate") return hasValueDate;
    return true;
  });

  const rows: string[] = [];
  if (header) {
    rows.push(cols.map((c) => escapeCsv(HEADERS[c], delimiter)).join(delimiter));
  }
  for (const t of result.transactions) {
    rows.push(cols.map((c) => escapeCsv(cell(t, c), delimiter)).join(delimiter));
  }
  // Trailing newline is friendlier to spreadsheet importers.
  return rows.join("\r\n") + "\r\n";
}

function cell(t: Transaction, col: (typeof COLUMNS)[number]): string {
  switch (col) {
    case "date":
      return t.date;
    case "valueDate":
      return t.valueDate ?? "";
    case "description":
      return t.description;
    case "amount":
      return formatNumber(t.amount);
    case "balance":
      return t.balance != null ? formatNumber(t.balance) : "";
  }
}

function formatNumber(n: number): string {
  // Machine-friendly dot decimal, no grouping — safe for re-import anywhere.
  return n.toFixed(2);
}

function escapeCsv(value: string, delimiter: string): string {
  if (
    value.includes(delimiter) ||
    value.includes('"') ||
    value.includes("\n") ||
    value.includes("\r")
  ) {
    return '"' + value.replace(/"/g, '""') + '"';
  }
  return value;
}
